import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { MergeView, getChunks, unifiedMergeView, type Chunk } from "@codemirror/merge";
import { EditorState, StateEffect, StateField, type Extension, type Range } from "@codemirror/state";
import { Decoration, EditorView, lineNumbers, type DecorationSet } from "@codemirror/view";
import { clsx } from "clsx";

import { RVRow } from "../../layouts/RVFlexbox/RVFlexbox";
import { RVButton } from "../../molecules/RVButton/RVButton";
import { RVNavigatorControl } from "../../molecules/RVNavigatorControl/RVNavigatorControl";
import { buildRegalCodeMirrorTheme } from "../RVCodeEditor/codeMirrorTheme";

import styles from "./RVCodeDiff.module.scss";

const DEFAULT_EXTENSIONS: readonly Extension[] = [];
const COLLAPSED_CONTEXT_LINES = 3;
const COLLAPSED_MINIMUM_LINES = 8;
const LARGE_DOCUMENT_SCAN_LIMIT = 1_000_000;
const TOP_ALIGN_PADDING_PX = 8;

export type RVCodeDiffViewMode = "split" | "unified";

export type RVCodeDiffScrollAlign = "top" | "center";

/** Imperative handle for driving change selection from an external navigator. */
export type RVCodeDiffHandle = {
    /** Select and scroll to the first changed chunk. */
    selectFirstChange: () => void;
    /** Select and scroll to the last changed chunk. */
    selectLastChange: () => void;
};
export type RVCodeDiffProps = {
    /** Text displayed as the original version. */
    original: string;
    /** Text displayed as the modified version. */
    modified: string;
    /** Optional CodeMirror extensions, such as a language parser. Keep this array reference stable. */
    extensions?: readonly Extension[];
    /** Initial layout. Defaults to unified mode. */
    initialViewMode?: RVCodeDiffViewMode;
    /** Optional class applied to the outer element. */
    className?: string;
    /** Optional content displayed on the left side of the panel header. */
    headerContent?: ReactNode;
    /** Whether to show editor line numbers. Defaults to true. */
    showLineNumbers?: boolean;
    /** Whether to show Split/Unified toggle controls. Defaults to true. */
    showViewModeControls?: boolean;
    /** How a navigated change is positioned in the viewport. Defaults to "center". */
    scrollAlign?: RVCodeDiffScrollAlign;
    /** When true, selects the first change on mount so the navigator opens on "1 of N". */
    autoSelectFirstChange?: boolean;
    /** Called when Next is pressed past the last change (or on a no-change diff) — hands off to an external navigator. */
    onNextBeyondEnd?: () => void;
    /** Called when Previous is pressed before the first change (or on a no-change diff) — hands off to an external navigator. */
    onPrevBeyondStart?: () => void;
};

type DiffViewHandle = {
    primary: EditorView;
    secondary?: EditorView;
    chunks: readonly Chunk[];
};

/** Effect carrying the document range of the actively-selected change, or null to clear it. */
const setActiveChangeRange = StateEffect.define<{ from: number; to: number } | null>();

function activeChangeDecorations(state: EditorState, from: number, to: number): DecorationSet {
    const lineDecoration = Decoration.line({ class: "cm-rv-active-change" });
    const ranges: Range<Decoration>[] = [];
    let pos = from;
    do {
        const line = state.doc.lineAt(pos);
        ranges.push(lineDecoration.range(line.from));
        pos = line.to + 1;
    } while (pos < to);
    return Decoration.set(ranges, true);
}

// Marks the active chunk's lines so a gutter bar renders only on the selected change.
const activeChangeField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(decorations, transaction) {
        let next = decorations.map(transaction.changes);
        for (const effect of transaction.effects) {
            if (effect.is(setActiveChangeRange)) {
                next = effect.value
                    ? activeChangeDecorations(transaction.state, effect.value.from, effect.value.to)
                    : Decoration.none;
            }
        }
        return next;
    },
    provide: (field) => EditorView.decorations.from(field),
});

function editorExtensions(theme: Extension, extensions: readonly Extension[], showLineNumbers: boolean): Extension[] {
    return [
        theme,
        ...(showLineNumbers ? [lineNumbers()] : []),
        EditorView.lineWrapping,
        EditorState.readOnly.of(true),
        EditorView.editable.of(false),
        activeChangeField,
        ...extensions,
    ];
}

/**
 * Finds the element that actually scrolls the editor.
 *
 * A side-by-side merge view forces each editor's own scroller to `overflow: visible`
 * and scrolls the wrapping `.cm-mergeView` instead, so the container is not always
 * the editor's `scrollDOM`.
 */
function findScrollContainer(from: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = from;
    while (current) {
        if (current.scrollHeight > current.clientHeight) {
            const overflowY = getComputedStyle(current).overflowY;
            if (overflowY === "auto" || overflowY === "scroll") {
                return current;
            }
        }
        current = current.parentElement;
    }
    return null;
}

/**
 * Scrolls a document position into the scroll container, aligned to the top or centered.
 *
 * Uses the height map rather than `EditorView.scrollIntoView` because that effect
 * needs the target line to already be rendered; positions beyond the rendered
 * viewport resolve to no coordinates and the scroll is silently dropped.
 */
function scrollPositionIntoView(view: EditorView, pos: number, align: RVCodeDiffScrollAlign): void {
    const container = findScrollContainer(view.scrollDOM);
    if (!container) {
        return;
    }

    const block = view.lineBlockAt(pos);
    const contentTop =
        view.contentDOM.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    const offset = align === "top" ? TOP_ALIGN_PADDING_PX : Math.max(0, (container.clientHeight - block.height) / 2);

    container.scrollTop = contentTop + block.top - offset;
}

function navigateToChunkIndex(handle: DiffViewHandle, index: number, align: RVCodeDiffScrollAlign): void {
    const chunk = handle.chunks[index];
    if (!chunk) {
        return;
    }

    handle.primary.dispatch({
        selection: { anchor: chunk.fromB },
        effects: setActiveChangeRange.of({ from: chunk.fromB, to: chunk.toB }),
        userEvent: "select.byChunk",
    });

    if (handle.secondary) {
        handle.secondary.dispatch({
            selection: { anchor: chunk.fromA },
            effects: setActiveChangeRange.of({ from: chunk.fromA, to: chunk.toA }),
            userEvent: "select.byChunk",
        });
    }

    scrollPositionIntoView(handle.primary, chunk.fromB, align);
}

function nextChangeIndex(currentIndex: number | undefined, direction: -1 | 1, chunkCount: number): number {
    if (currentIndex === undefined) {
        return direction === 1 ? 0 : chunkCount - 1;
    }
    return (currentIndex + direction + chunkCount) % chunkCount;
}

/**
 * Read-only CodeMirror diff viewer with split/unified layouts and change navigation.
 *
 * Long unchanged ranges are collapsed to keep large documents easy to scan.
 */
export const RVCodeDiff = forwardRef<RVCodeDiffHandle, RVCodeDiffProps>(function RVCodeDiff(
    {
        original,
        modified,
        extensions = DEFAULT_EXTENSIONS,
        initialViewMode = "unified",
        className,
        headerContent,
        showLineNumbers = true,
        showViewModeControls = true,
        scrollAlign = "center",
        autoSelectFirstChange = false,
        onNextBeyondEnd,
        onPrevBeyondStart,
    },
    ref
): JSX.Element {
    const hostRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<DiffViewHandle>();
    const [viewMode, setViewMode] = useState<RVCodeDiffViewMode>(initialViewMode);
    const [changeCount, setChangeCount] = useState(0);
    const [activeChangeIndex, setActiveChangeIndex] = useState<number>();
    const theme = useMemo(() => buildRegalCodeMirrorTheme(), []);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) {
            return;
        }

        setActiveChangeIndex(undefined);
        const commonExtensions = editorExtensions(theme, extensions, showLineNumbers);

        if (viewMode === "split") {
            const mergeView = new MergeView({
                parent: host,
                a: { doc: original, extensions: commonExtensions },
                b: { doc: modified, extensions: commonExtensions },
                collapseUnchanged: {
                    margin: COLLAPSED_CONTEXT_LINES,
                    minSize: COLLAPSED_MINIMUM_LINES,
                },
                highlightChanges: true,
                gutter: true,
                diffConfig: {
                    scanLimit: LARGE_DOCUMENT_SCAN_LIMIT,
                },
            });
            viewRef.current = {
                primary: mergeView.b,
                secondary: mergeView.a,
                chunks: mergeView.chunks,
            };
            setChangeCount(mergeView.chunks.length);

            return () => {
                viewRef.current = undefined;
                mergeView.destroy();
            };
        }

        const unifiedView = new EditorView({
            parent: host,
            doc: modified,
            extensions: [
                ...commonExtensions,
                unifiedMergeView({
                    original,
                    mergeControls: false,
                    allowInlineDiffs: true,
                    collapseUnchanged: {
                        margin: COLLAPSED_CONTEXT_LINES,
                        minSize: COLLAPSED_MINIMUM_LINES,
                    },
                    diffConfig: {
                        scanLimit: LARGE_DOCUMENT_SCAN_LIMIT,
                    },
                }),
            ],
        });
        const chunks = getChunks(unifiedView.state)?.chunks ?? [];
        viewRef.current = { primary: unifiedView, chunks };
        setChangeCount(chunks.length);

        return () => {
            viewRef.current = undefined;
            unifiedView.destroy();
        };
    }, [extensions, modified, original, showLineNumbers, theme, viewMode]);

    const navigateToChange = useCallback(
        (direction: -1 | 1): void => {
            const handle = viewRef.current;
            const chunkCount = handle?.chunks.length ?? 0;

            // No changes in this diff — step straight to the adjacent block via the external navigator.
            if (!handle || chunkCount === 0) {
                (direction === 1 ? onNextBeyondEnd : onPrevBeyondStart)?.();
                return;
            }

            // At a boundary, hand off to the adjacent block instead of wrapping.
            if (direction === 1 && onNextBeyondEnd && activeChangeIndex === chunkCount - 1) {
                onNextBeyondEnd();
                return;
            }
            if (direction === -1 && onPrevBeyondStart && activeChangeIndex === 0) {
                onPrevBeyondStart();
                return;
            }

            const nextIndex = nextChangeIndex(activeChangeIndex, direction, chunkCount);
            setActiveChangeIndex(nextIndex);
            navigateToChunkIndex(handle, nextIndex, scrollAlign);
        },
        [activeChangeIndex, onNextBeyondEnd, onPrevBeyondStart, scrollAlign]
    );

    const selectFirstChange = useCallback((): void => {
        const handle = viewRef.current;
        if (!handle || handle.chunks.length === 0) {
            return;
        }
        setActiveChangeIndex(0);
        navigateToChunkIndex(handle, 0, scrollAlign);
    }, [scrollAlign]);

    const selectLastChange = useCallback((): void => {
        const handle = viewRef.current;
        if (!handle || handle.chunks.length === 0) {
            return;
        }
        const lastIndex = handle.chunks.length - 1;
        setActiveChangeIndex(lastIndex);
        navigateToChunkIndex(handle, lastIndex, scrollAlign);
    }, [scrollAlign]);

    useImperativeHandle(ref, () => ({ selectFirstChange, selectLastChange }), [selectFirstChange, selectLastChange]);

    // Open on the first change so the navigator reads "1 of N" instead of the idle count.
    useEffect(() => {
        if (!autoSelectFirstChange) {
            return;
        }
        const frame = requestAnimationFrame(() => selectFirstChange());
        return () => cancelAnimationFrame(frame);
    }, [autoSelectFirstChange, selectFirstChange, viewMode, original, modified]);

    // With a handoff callback, the arrows stay live even on a no-change diff so they can jump to the adjacent block.
    const canNavigatePrev = changeCount > 0 || onPrevBeyondStart != null;
    const canNavigateNext = changeCount > 0 || onNextBeyondEnd != null;
    const navigationLabel =
        activeChangeIndex === undefined
            ? `${changeCount} changes`
            : `${activeChangeIndex + 1} of ${changeCount} changes`;

    return (
        <section className={clsx(styles.diff, className)} aria-label="Text comparison">
            <RVRow
                justifyContent={headerContent ? "space-between" : "flex-end"}
                alignItems="center"
                className={styles.toolbar}
            >
                {headerContent ? <div className={styles.headerContent}>{headerContent}</div> : null}
                <RVRow className={styles.controls} gap={2} alignItems="center">
                    {showViewModeControls && (
                        <>
                            <RVButton
                                size="small"
                                type={viewMode === "split" ? "primary" : "default"}
                                onClick={() => setViewMode("split")}
                            >
                                <span>Split</span>
                            </RVButton>
                            <RVButton
                                size="small"
                                type={viewMode === "unified" ? "primary" : "default"}
                                onClick={() => setViewMode("unified")}
                            >
                                <span>Unified</span>
                            </RVButton>
                        </>
                    )}
                    {changeCount === 1 ? (
                        <span className={styles.changeCountLabel} role="status">
                            1 Change
                        </span>
                    ) : (
                        <RVNavigatorControl
                            className={styles.navigator}
                            label={<span role="status">{navigationLabel}</span>}
                            previousAriaLabel="Previous change"
                            nextAriaLabel="Next change"
                            prevDisabled={!canNavigatePrev}
                            nextDisabled={!canNavigateNext}
                            onPrev={() => navigateToChange(-1)}
                            onNext={() => navigateToChange(1)}
                        />
                    )}
                </RVRow>
            </RVRow>
            <div ref={hostRef} className={styles.editorHost} data-testid="code-diff-editor" />
        </section>
    );
});
